/**
 * HLF Floating Cart Bar
 *
 * Two panels docked to the bottom of the viewport: the next gift milestone on
 * the left, the cart summary on the right.
 *
 * The milestone is computed here rather than in Liquid because the cart changes
 * over AJAX and this component is never re-rendered by a section update. The
 * ladder itself comes from Liquid via data-tiers (snippets/hlf-gift-tiers-json),
 * so the thresholds and labels stay in one place.
 */
window.hlfFloatingCart = {
  RING_CIRCUMFERENCE: 106.81, // 2πr for r=17, matching the CSS stroke-dasharray

  init() {
    this.element = document.getElementById('hlf-floating-cart');
    if (!this.element) return;

    this.thumbEl = document.getElementById('hlf-floating-cart-thumbnails');
    this.countEl = document.getElementById('hlf-floating-cart-count');
    this.milestoneEl = this.element.querySelector('[data-hlf-fc-milestone]');
    this.titleEl = this.element.querySelector('[data-hlf-fc-title]');
    this.subEl = this.element.querySelector('[data-hlf-fc-sub]');
    this.ringEl = this.element.querySelector('[data-hlf-fc-ring]');
    this.ringImgEl = this.element.querySelector('[data-hlf-fc-img]');
    this.ringFallbackEl = this.element.querySelector('[data-hlf-fc-fallback]');

    this.tiers = [];
    const raw = this.element.getAttribute('data-tiers');
    if (raw) {
      try {
        this.tiers = JSON.parse(raw) || [];
      } catch (e) {
        this.tiers = [];
      }
    }

    this.previousCount = null;
    this.fetchCart(true);

    let timeout;
    const handleUpdate = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => this.fetchCart(false), 50);
    };

    if (window.PUB_SUB_EVENTS && window.subscribe) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, handleUpdate);
    }
    document.addEventListener('cart:update', handleUpdate);
    document.addEventListener('cart:updated', handleUpdate);
    document.addEventListener('cart:refresh', handleUpdate);
  },

  async fetchCart(isInitial) {
    try {
      const cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
      const response = await fetch(cartUrl + '.js');
      const cart = await response.json();
      this.render(cart, isInitial);
    } catch (e) {
      /* leave the bar as-is on a transient failure */
    }
  },

  render(cart, isInitial) {
    if (!this.element || !cart) return;

    if (cart.item_count === 0) {
      this.element.classList.remove('is-visible', 'is-item-added');
      this.previousCount = 0;
      return;
    }

    const grew = !isInitial && this.previousCount !== null && cart.item_count > this.previousCount;
    this.previousCount = cart.item_count;

    // Cart panel
    if (this.countEl) {
      this.countEl.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    }
    const image = cart.items.length ? cart.items[0].image : null;
    if (this.thumbEl) {
      this.thumbEl.textContent = '';
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.width = 44;
        img.height = 44;
        img.loading = 'lazy';
        this.thumbEl.appendChild(img);
      }
    }

    this.renderMilestone(cart.total_price);
    this.element.classList.add('is-visible');

    if (grew) {
      this.element.classList.add('is-item-added');
      setTimeout(() => this.element.classList.remove('is-item-added'), 450);
    }
  },

  /**
   * Show the highest tier already won alongside the next one.
   *
   * Naming the reward already banked ("Free Shaker unlocked") turns the next ask
   * into an upgrade rather than a bill, which is what loss aversion actually
   * responds to. Before any tier is cleared there is nothing to lose yet, so it
   * falls back to the plain "Unlock X" framing.
   *
   * At the top tier the panel stays and confirms the reward rather than hiding.
   * Only the highest tier reached is actually awarded, so this is the shopper's
   * standing confirmation of what they have earned — dropping it there left the
   * bar looking half-built at exactly the moment they had won the most.
   */
  renderMilestone(totalPaise) {
    if (!this.milestoneEl) return;
    if (!this.tiers.length) {
      this.milestoneEl.hidden = true;
      return;
    }

    let previousPaise = 0;
    let next = null;
    let unlocked = null;
    for (let i = 0; i < this.tiers.length; i++) {
      if (totalPaise < this.tiers[i].paise) {
        next = this.tiers[i];
        break;
      }
      previousPaise = this.tiers[i].paise;
      unlocked = this.tiers[i];
    }

    // Top tier reached: nothing further to work toward, so the panel becomes a
    // confirmation of the reward earned instead of disappearing.
    const atTop = !next;
    const reward = next || unlocked;
    if (!reward) {
      this.milestoneEl.hidden = true;
      return;
    }

    const span = atTop ? 0 : reward.paise - previousPaise;
    const into = totalPaise - previousPaise;
    const fraction = atTop ? 1 : (span > 0 ? Math.min(1, Math.max(0, into / span)) : 0);

    if (this.ringEl) {
      this.ringEl.style.strokeDashoffset = String(this.RING_CIRCUMFERENCE * (1 - fraction));
    }

    // Show the reward being worked toward. Falls back to the icon when the tier
    // has no product assigned, so a half-configured ladder still renders.
    if (this.ringImgEl && this.ringFallbackEl) {
      if (reward.image) {
        if (this.ringImgEl.getAttribute('src') !== reward.image) {
          this.ringImgEl.setAttribute('src', reward.image);
        }
        this.ringImgEl.hidden = false;
        this.ringFallbackEl.style.display = 'none';
      } else {
        this.ringImgEl.hidden = true;
        this.ringFallbackEl.style.display = '';
      }
    }

    // Built with textContent throughout, so a label from theme settings can
    // never inject markup.
    if (this.titleEl) {
      this.titleEl.textContent = unlocked ? unlocked.label + ' unlocked' : 'Unlock ' + reward.label;
    }
    if (this.subEl) {
      this.subEl.textContent = '';
      if (atTop) {
        // Only the highest tier reached is awarded, so this names that gift
        // rather than claiming every gift on the ladder.
        this.subEl.appendChild(document.createTextNode('Our biggest reward is yours'));
      } else {
        const amount = document.createElement('b');
        amount.textContent = '₹' + Math.ceil((reward.paise - totalPaise) / 100).toLocaleString('en-IN');
        if (unlocked) {
          this.subEl.appendChild(amount);
          this.subEl.appendChild(document.createTextNode(' more → ' + reward.label));
        } else {
          this.subEl.appendChild(document.createTextNode('Shop for '));
          this.subEl.appendChild(amount);
          this.subEl.appendChild(document.createTextNode(' more'));
        }
      }
    }

    this.milestoneEl.classList.toggle('has-unlocked', Boolean(unlocked));
    this.milestoneEl.hidden = false;
  },

  openCart() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.open) {
      cartDrawer.open();
      return;
    }
    const cartIcon = document.querySelector('#cart-icon-bubble, [data-hlf-cart-trigger], .header__icon--cart');
    if (cartIcon) {
      cartIcon.click();
      return;
    }
    window.location.href = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
  }
};

document.addEventListener('DOMContentLoaded', () => {
  window.hlfFloatingCart.init();
});
